import { getDeep } from '../utils/getDeep.js';

const undoRedoKeys = {
  isSkipped: 'primitive',
  actionId: 'primitive',
  appId: 'primitive',
  functionId: 'primitive',
  appVersion: 'primitive',
  'options.functionSnapshotId': 'primitive',
  arguments: 'object',
  'options.inputArguments': 'array',
  'options.flowControl.onFailure': 'object',
  'options.flowControl.onNoResults': 'object',
  'options.pagination': 'object',
  'options.sortCriteria': 'array',
  'options.filters': 'array',
  'options.slices': 'array',
  'options.cache': 'object',
  'options.customArguments': 'object',
  'options.conditionalAuth': 'array',
  'options.memory': 'array',
  'options.notes': 'primitive',
  'options.logging': 'object'
};

const allKeys = {
  ...undoRedoKeys, //
  credentialId: 'primitive',
  message: 'primitive',
  isValid: 'primitive',
  meta: 'object',
  type: 'primitive',
  externalCompanyId: 'primitive',
  isMessageManual: 'primitive',
  appSlug: 'primitive',
  createdOn: 'primitive',
  createdBy: 'primitive',
  updatedOn: 'primitive',
  updatedBy: 'primitive'
};

const hashCode = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

const findArrayChanges = ({ type, oldValue = [], newValue = [] }) => {
  const mapItems = (items) => new Map(items?.map?.((item) => [item.id, item]));
  const newMap = mapItems(newValue);
  const changes = [];

  const addChange = (operation, id, oldValue, newValue) => {
    changes.push({ type, operation, id, oldValue, newValue });
  };

  oldValue.forEach((item) => {
    if (!newMap.has(item.id)) {
      addChange('delete', item.id, item, undefined);
    } else {
      const newItem = newMap.get(item.id);
      if (JSON.stringify(item) !== JSON.stringify(newItem)) {
        addChange('modify', item.id, item, newItem);
      }
      newMap.delete(item.id); // Remove from map to avoid rechecking
    }
  });

  // Check for additions
  newValue.forEach((item) => {
    if (newMap.has(item.id)) {
      addChange('add', item.id, undefined, item);
    }
  });

  return changes;
};

const buildPayload = ({ changes, userId, stepId }) => ({
  id: hashCode(JSON.stringify(changes)),
  changedOn: new Date().toISOString(),
  userId,
  stepId,
  changes
});

export const getStepChangeDiff = ({ userId, originalStep, updatedStep, undoRedo = false }) => {
  const changes = [];
  const stepId = originalStep?.id;

  const getChanges = ({ type, key, index, oldValue, newValue }) => {
    const oldValueString = JSON.stringify(oldValue);
    const newValueString = JSON.stringify(newValue);
    if (oldValueString !== newValueString) {
      return [{ type, key, oldValue, newValue }];
    }
    return [];
  };

  if (originalStep === null && !!updatedStep) {
    changes.push(...getChanges({ type: 'step', oldValue: null, newValue: updatedStep }));
    return buildPayload({ changes, userId, stepId });
  }

  if (updatedStep === null && !!originalStep) {
    changes.push(...getChanges({ type: 'step', oldValue: originalStep, newValue: null }));
    return buildPayload({ changes, userId, stepId });
  }

  const topLevelKeyMap = undoRedo ? undoRedoKeys : allKeys;
  const topLevelKeys = Object.keys(topLevelKeyMap);

  for (let i = 0; i < topLevelKeys.length; i++) {
    const topLevelKey = topLevelKeys[i];
    const topLevelKeyType = topLevelKeyMap[topLevelKey];
    const oldParent = getDeep(topLevelKey, originalStep || {});
    const newParent = getDeep(topLevelKey, updatedStep || {});

    if (newParent === undefined) {
      continue;
    }

    if (topLevelKeyType === 'array') {
      const arrayChanges = findArrayChanges({ type: topLevelKey, oldValue: oldParent, newValue: newParent });
      changes.push(...arrayChanges);
    } else if (topLevelKeyType === 'object' && newParent !== null) {
      const newKeys = Object.keys(newParent || {});
      const oldKeys = Object.keys(oldParent || {}).filter((key) => !newKeys.includes(key));
      for (const childKey in newParent) {
        const oldValue = oldParent?.[childKey];
        const newValue = newParent?.[childKey];
        if (topLevelKey === 'meta' && childKey === 'changes') {
          continue;
        }
        changes.push(...getChanges({ type: topLevelKey, key: childKey, oldValue, newValue }));
      }
      for (let j = 0; j < oldKeys.length; j++) {
        const childKey = oldKeys[j];
        const oldValue = oldParent?.[childKey];
        const newValue = newParent?.[childKey];
        changes.push(...getChanges({ type: topLevelKey, key: childKey, oldValue, newValue }));
      }
    } else if (topLevelKeyType === 'object' && newParent === null) {
      changes.push(...getChanges({ type: topLevelKey, key: '', oldValue: oldParent, newValue: newParent }));
    } else if (topLevelKeyType === 'primitive') {
      const newChanges = getChanges({ type: topLevelKey, key: '', oldValue: oldParent, newValue: newParent });
      changes.push(...newChanges);
    }
  }

  if (changes.length === 0) {
    return undefined;
  }

  return buildPayload({ changes, userId, stepId });
};
